import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Target, DollarSign, TrendingUp, TrendingDown, ShoppingCart, Wallet, History, Plus, Minus, RefreshCw, ArrowUpRight, ArrowDownRight, HelpCircle, X, BookOpen, ChevronRight } from "lucide-react";
import { API } from "../App";

const PaperTrading = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [marketData, setMarketData] = useState({ crypto: [], stocks: [], commodities: [] });
  const [activeMarket, setActiveMarket] = useState("crypto");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [tradeQuantity, setTradeQuantity] = useState("");
  const [tradeHistory, setTradeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  const [showGuide, setShowGuide] = useState(false);
  
  // Initialize or load portfolio
  useEffect(() => {
    const initPortfolio = async () => {
      const savedId = localStorage.getItem('portfolioId');
      
      if (savedId) {
        try {
          const response = await axios.get(`${API}/portfolio/${savedId}`);
          setPortfolio(response.data);
          
          const historyRes = await axios.get(`${API}/portfolio/${savedId}/history`);
          setTradeHistory(historyRes.data.trades || []);
        } catch (error) {
          // Portfolio not found, create new
          await createNewPortfolio();
        }
      } else {
        await createNewPortfolio();
      }
      setLoading(false);
    };
    
    initPortfolio();
  }, []);
  
  const createNewPortfolio = async () => {
    try {
      const response = await axios.post(`${API}/portfolio/create`);
      setPortfolio(response.data);
      localStorage.setItem('portfolioId', response.data.id);
      setTradeHistory([]);
    } catch (error) {
      console.error("Error creating portfolio:", error);
    }
  };
  
  // Fetch market data
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const response = await axios.get(`${API}/market/all`);
        setMarketData({
          crypto: response.data.crypto || [],
          stocks: response.data.stocks || [],
          commodities: response.data.commodities || []
        });
      } catch (error) {
        console.error("Error fetching market data:", error);
      }
    };
    
    fetchMarketData();
    const interval = setInterval(fetchMarketData, 60000);
    return () => clearInterval(interval);
  }, []);
  
  const executeTrade = async (action) => {
    if (!selectedAsset || !tradeQuantity || tradeQuantity <= 0) {
      setMessage({ type: 'error', text: 'Please select an asset and enter quantity' });
      return;
    }
    
    setTradeLoading(true);
    
    try {
      const response = await axios.post(`${API}/portfolio/${portfolio.id}/trade`, {
        symbol: selectedAsset.symbol,
        name: selectedAsset.name,
        quantity: parseFloat(tradeQuantity),
        price: selectedAsset.price,
        action: action,
        market_type: selectedAsset.market_type
      });
      
      setPortfolio(response.data.portfolio);
      setMessage({ 
        type: 'success', 
        text: `Successfully ${action === 'buy' ? 'bought' : 'sold'} ${tradeQuantity} ${selectedAsset.symbol}` 
      });
      setTradeQuantity("");
      
      // Refresh history
      const historyRes = await axios.get(`${API}/portfolio/${portfolio.id}/history`);
      setTradeHistory(historyRes.data.trades || []);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Trade failed. Please try again.' 
      });
    } finally {
      setTradeLoading(false);
    }
  };
  
  const resetPortfolio = async () => {
    if (window.confirm('Are you sure you want to reset your portfolio? This will clear all holdings and history.')) {
      await createNewPortfolio();
      setMessage({ type: 'success', text: 'Portfolio reset successfully!' });
    }
  };
  
  const currentData = marketData[activeMarket] || [];
  
  // Calculate P&L for holdings
  const calculatePnL = (holding) => {
    const pnl = (holding.current_price - holding.avg_price) * holding.quantity;
    const pnlPercent = ((holding.current_price - holding.avg_price) / holding.avg_price) * 100;
    return { pnl, pnlPercent };
  };
  
  const totalPnL = portfolio?.holdings?.reduce((sum, h) => {
    const { pnl } = calculatePnL(h);
    return sum + pnl;
  }, 0) || 0;
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <p>Setting up your paper trading account...</p>
      </div>
    );
  }
  
  return (
    <div className="paper-trading" data-testid="paper-trading">
      <div className="container">
        {/* Header */}
        <div className="trading-header" data-testid="trading-header">
          <div className="trading-header-top">
            <div>
              <h1>Paper Trading Simulator</h1>
              <p>Practice trading with $10,000 virtual money — no real money at risk!</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="guide-btn" onClick={() => setShowGuide(true)} data-testid="show-guide-btn">
              <HelpCircle size={18} />
              How To Use
            </button>
            <button className="reset-btn" onClick={resetPortfolio} data-testid="reset-portfolio-btn">
              <RefreshCw size={18} />
              Reset Portfolio
            </button>
          </div>
        </div>
        
        {/* How To Use Guide Modal */}
        {showGuide && (
          <motion.div 
            className="guide-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setShowGuide(false)}
          >
            <motion.div 
              className="guide-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="guide-modal"
            >
              <button className="guide-close" onClick={() => setShowGuide(false)}>
                <X size={24} />
              </button>
              
              <div className="guide-header">
                <BookOpen size={32} />
                <h2>How To Use The Trading Simulator</h2>
                <p>Learn to trade risk-free with virtual money</p>
              </div>
              
              <div className="guide-content">
                <div className="guide-section">
                  <div className="guide-step">
                    <span className="step-number">1</span>
                    <div>
                      <h4>Start With Virtual Cash</h4>
                      <p>You begin with <strong>$10,000 in virtual money</strong>. This isn't real — it's for practice! You can reset anytime to start fresh.</p>
                    </div>
                  </div>
                  
                  <div className="guide-step">
                    <span className="step-number">2</span>
                    <div>
                      <h4>Choose Your Market</h4>
                      <p>Click the tabs <strong>(Crypto, Stocks, Commodities)</strong> to switch between different markets. Each has different assets to trade.</p>
                    </div>
                  </div>
                  
                  <div className="guide-step">
                    <span className="step-number">3</span>
                    <div>
                      <h4>Select An Asset</h4>
                      <p>Click on any asset (like Bitcoin or Apple) from the list. You'll see its <strong>current price</strong> and <strong>24h change</strong>. The selected asset will be highlighted.</p>
                    </div>
                  </div>
                  
                  <div className="guide-step">
                    <span className="step-number">4</span>
                    <div>
                      <h4>Enter Your Quantity</h4>
                      <p>Type how many units you want to buy or sell. The <strong>"Total Cost"</strong> shows you exactly how much cash you'll spend. Use the <strong>+/- buttons</strong> for quick adjustments.</p>
                    </div>
                  </div>
                  
                  <div className="guide-step">
                    <span className="step-number">5</span>
                    <div>
                      <h4>Execute Your Trade</h4>
                      <p><strong>BUY</strong> = You think the price will go UP (you want to buy low, sell high)<br/>
                      <strong>SELL</strong> = You own the asset and want to take profit or cut losses</p>
                    </div>
                  </div>
                  
                  <div className="guide-step">
                    <span className="step-number">6</span>
                    <div>
                      <h4>Track Your Performance</h4>
                      <p>Watch your <strong>Portfolio Summary</strong> at the top. The <strong>P&L (Profit/Loss)</strong> shows if you're winning or losing. Check your <strong>Holdings</strong> to see what you own.</p>
                    </div>
                  </div>
                </div>
                
                <div className="guide-tips">
                  <h4>💡 Pro Tips</h4>
                  <ul>
                    <li><ChevronRight size={14} /> <strong>Don't go all-in:</strong> Real traders never put all their money in one asset</li>
                    <li><ChevronRight size={14} /> <strong>Watch the 24h change:</strong> Negative % means price dropped recently</li>
                    <li><ChevronRight size={14} /> <strong>Practice different strategies:</strong> Try buying dips, following trends, or holding long-term</li>
                    <li><ChevronRight size={14} /> <strong>Review your trade history:</strong> Learn from both your wins and losses</li>
                    <li><ChevronRight size={14} /> <strong>Reset when needed:</strong> Made mistakes? Reset and apply what you learned!</li>
                  </ul>
                </div>
                
                <div className="guide-cta">
                  <button className="guide-cta-btn" onClick={() => setShowGuide(false)}>
                    Got It — Let's Trade!
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Message */}
        {message && (
          <motion.div 
            className={`message ${message.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="trade-message"
          >
            {message.text}
            <button onClick={() => setMessage(null)}>×</button>
          </motion.div>
        )}
        
        {/* Portfolio Summary */}
        <div className="portfolio-summary" data-testid="portfolio-summary">
          <div className="summary-card total">
            <Wallet size={24} />
            <div>
              <span className="label">Total Value</span>
              <span className="value">${portfolio?.total_value?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          <div className="summary-card cash">
            <DollarSign size={24} />
            <div>
              <span className="label">Cash Balance</span>
              <span className="value">${portfolio?.cash_balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          <div className={`summary-card pnl ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {totalPnL >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
            <div>
              <span className="label">Total P&L</span>
              <span className="value">
                {totalPnL >= 0 ? '+' : ''}
                ${totalPnL.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          <div className="summary-card holdings">
            <Target size={24} />
            <div>
              <span className="label">Holdings</span>
              <span className="value">{portfolio?.holdings?.length || 0} assets</span>
            </div>
          </div>
        </div>
        
        <div className="trading-layout">
          {/* Left: Market Selection */}
          <div className="market-panel" data-testid="market-panel">
            <div className="panel-header">
              <h3>Select Asset</h3>
              <div className="market-tabs">
                {['crypto', 'stocks', 'commodities'].map(market => (
                  <button
                    key={market}
                    className={`market-tab ${activeMarket === market ? 'active' : ''}`}
                    onClick={() => { setActiveMarket(market); setSelectedAsset(null); }}
                    data-testid={`market-tab-${market}`}
                  >
                    {market.charAt(0).toUpperCase() + market.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="assets-list" data-testid="assets-list">
              {currentData.map((asset, index) => (
                <button
                  key={index}
                  className={`asset-item ${selectedAsset?.symbol === asset.symbol ? 'selected' : ''}`}
                  onClick={() => setSelectedAsset(asset)}
                  data-testid={`asset-${asset.symbol}`}
                >
                  <div className="asset-info">
                    {asset.image ? (
                      <img src={asset.image} alt={asset.name} className="asset-icon" />
                    ) : (
                      <div className="asset-icon-placeholder">{asset.symbol.charAt(0)}</div>
                    )}
                    <div>
                      <span className="symbol">{asset.symbol}</span>
                      <span className="name">{asset.name}</span>
                    </div>
                  </div>
                  <div className="asset-price">
                    <span className="price">${asset.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: asset.price > 1 ? 2 : 6 })}</span>
                    <span className={`change ${asset.change_24h >= 0 ? 'positive' : 'negative'}`}>
                      {asset.change_24h >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                      {Math.abs(asset.change_24h || 0).toFixed(2)}%
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Center: Trade Panel */}
          <div className="trade-panel" data-testid="trade-panel">
            <div className="panel-header">
              <h3>Place Trade</h3>
            </div>
            
            {selectedAsset ? (
              <div className="trade-form">
                <div className="selected-asset-display">
                  <div className="asset-header">
                    {selectedAsset.image ? (
                      <img src={selectedAsset.image} alt={selectedAsset.name} className="asset-icon-lg" />
                    ) : (
                      <div className="asset-icon-placeholder lg">{selectedAsset.symbol.charAt(0)}</div>
                    )}
                    <div>
                      <h4>{selectedAsset.symbol}</h4>
                      <p>{selectedAsset.name}</p>
                    </div>
                  </div>
                  <div className="current-price">
                    <span className="label">Current Price</span>
                    <span className="price">${selectedAsset.price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: selectedAsset.price > 1 ? 2 : 6 })}</span>
                  </div>
                </div>
                
                <div className="quantity-input">
                  <label>Quantity</label>
                  <div className="input-wrapper">
                    <button onClick={() => setTradeQuantity(prev => Math.max(0, (parseFloat(prev) || 0) - 1).toString())}>
                      <Minus size={18} />
                    </button>
                    <input
                      type="number"
                      value={tradeQuantity}
                      onChange={(e) => setTradeQuantity(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="any"
                      data-testid="quantity-input"
                    />
                    <button onClick={() => setTradeQuantity(prev => ((parseFloat(prev) || 0) + 1).toString())}>
                      <Plus size={18} />
                    </button>
                  </div>
                  {tradeQuantity && selectedAsset && (
                    <p className="total-cost">
                      Total: ${(parseFloat(tradeQuantity) * selectedAsset.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
                
                <div className="trade-buttons">
                  <button 
                    className="btn btn-success"
                    onClick={() => executeTrade('buy')}
                    disabled={tradeLoading}
                    data-testid="buy-btn"
                  >
                    <ShoppingCart size={18} />
                    Buy
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => executeTrade('sell')}
                    disabled={tradeLoading}
                    data-testid="sell-btn"
                  >
                    <DollarSign size={18} />
                    Sell
                  </button>
                </div>
              </div>
            ) : (
              <div className="no-asset-selected">
                <Target size={48} />
                <p>Select an asset from the list to start trading</p>
              </div>
            )}
          </div>
          
          {/* Right: Holdings & History */}
          <div className="holdings-panel" data-testid="holdings-panel">
            <div className="panel-header">
              <h3>Your Holdings</h3>
            </div>
            
            {portfolio?.holdings?.length > 0 ? (
              <div className="holdings-list">
                {portfolio.holdings.map((holding, index) => {
                  const { pnl, pnlPercent } = calculatePnL(holding);
                  return (
                    <div key={index} className="holding-item" data-testid={`holding-${holding.symbol}`}>
                      <div className="holding-info">
                        <span className="symbol">{holding.symbol}</span>
                        <span className="quantity">{holding.quantity.toFixed(4)} units</span>
                      </div>
                      <div className="holding-value">
                        <span className="current">${(holding.quantity * holding.current_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        <span className={`pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
                          {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} ({pnlPercent.toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-holdings">
                <p>No holdings yet. Start trading!</p>
              </div>
            )}
            
            {/* Trade History */}
            <div className="history-section" data-testid="history-section">
              <div className="panel-header">
                <h3><History size={18} /> Recent Trades</h3>
              </div>
              {tradeHistory.length > 0 ? (
                <div className="history-list">
                  {tradeHistory.slice(0, 10).map((trade, index) => (
                    <div key={index} className={`history-item ${trade.action}`}>
                      <div className="trade-info">
                        <span className="action">{trade.action.toUpperCase()}</span>
                        <span className="details">{trade.quantity} {trade.symbol}</span>
                      </div>
                      <span className="total">${trade.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-history">No trades yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .paper-trading {
          padding: 40px 0 80px;
        }
        
        .trading-header {
          margin-bottom: 24px;
        }
        
        .trading-header-top {
          margin-bottom: 16px;
        }
        
        .trading-header h1 {
          font-size: 2rem;
          margin: 0 0 8px 0;
        }
        
        .trading-header p {
          color: var(--text-secondary);
          margin: 0;
        }
        
        .header-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .guide-btn {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          padding: 12px 24px !important;
          background: linear-gradient(135deg, #00ff94 0%, #00e085 100%) !important;
          border: 2px solid #00ff94 !important;
          border-radius: 8px !important;
          color: #0a0a0f !important;
          font-weight: 700 !important;
          font-size: 15px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 2px 10px rgba(0, 255, 148, 0.3) !important;
        }
        
        .guide-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0, 255, 148, 0.3);
        }
        
        .reset-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          cursor: pointer;
        }
        
        .reset-btn:hover {
          border-color: var(--error);
          color: var(--error);
        }
        
        /* Guide Modal */
        .guide-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .guide-modal {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 20px;
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }
        
        .guide-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 8px;
          border-radius: 50%;
          transition: all 0.2s;
        }
        
        .guide-close:hover {
          background: var(--bg-subtle);
          color: var(--text-primary);
        }
        
        .guide-header {
          text-align: center;
          padding: 32px 32px 24px;
          border-bottom: 1px solid var(--border);
        }
        
        .guide-header svg {
          color: var(--primary);
          margin-bottom: 12px;
        }
        
        .guide-header h2 {
          font-size: 1.75rem;
          margin-bottom: 8px;
        }
        
        .guide-header p {
          color: var(--text-muted);
        }
        
        .guide-content {
          padding: 24px 32px 32px;
        }
        
        .guide-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-bottom: 32px;
        }
        
        .guide-step {
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }
        
        .step-number {
          width: 32px;
          height: 32px;
          min-width: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary);
          color: #0a0a0f;
          border-radius: 50%;
          font-weight: 700;
          font-size: 14px;
        }
        
        .guide-step h4 {
          font-size: 1.1rem;
          margin-bottom: 6px;
          color: var(--text-primary);
        }
        
        .guide-step p {
          color: var(--text-secondary);
          line-height: 1.6;
        }
        
        .guide-step strong {
          color: var(--primary);
        }
        
        .guide-tips {
          background: var(--bg-subtle);
          border-radius: 12px;
          padding: 20px 24px;
          margin-bottom: 24px;
        }
        
        .guide-tips h4 {
          font-size: 1.1rem;
          margin-bottom: 16px;
        }
        
        .guide-tips ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .guide-tips li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 12px;
          color: var(--text-secondary);
          line-height: 1.5;
        }
        
        .guide-tips li:last-child {
          margin-bottom: 0;
        }
        
        .guide-tips li svg {
          color: var(--primary);
          margin-top: 4px;
          flex-shrink: 0;
        }
        
        .guide-tips li strong {
          color: var(--text-primary);
        }
        
        .guide-cta {
          text-align: center;
        }
        
        .guide-cta-btn {
          padding: 14px 32px;
          background: linear-gradient(135deg, var(--primary) 0%, #00e085 100%);
          border: none;
          border-radius: 50px;
          color: #0a0a0f;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .guide-cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(0, 255, 148, 0.4);
        }
        
        /* Message */
        .message {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-radius: 8px;
          margin-bottom: 24px;
        }
        
        .message.success {
          background: var(--success-bg);
          color: var(--success);
        }
        
        .message.error {
          background: var(--error-bg);
          color: var(--error);
        }
        
        .message button {
          background: none;
          border: none;
          color: inherit;
          font-size: 20px;
          cursor: pointer;
        }
        
        /* Portfolio Summary */
        .portfolio-summary {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }
        
        .summary-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .summary-card svg {
          color: var(--text-muted);
        }
        
        .summary-card .label {
          display: block;
          font-size: 13px;
          color: var(--text-muted);
          margin-bottom: 4px;
        }
        
        .summary-card .value {
          display: block;
          font-family: var(--font-mono);
          font-size: 1.25rem;
          font-weight: 600;
        }
        
        .summary-card.pnl.positive { border-color: var(--success); }
        .summary-card.pnl.positive .value { color: var(--success); }
        .summary-card.pnl.positive svg { color: var(--success); }
        
        .summary-card.pnl.negative { border-color: var(--error); }
        .summary-card.pnl.negative .value { color: var(--error); }
        .summary-card.pnl.negative svg { color: var(--error); }
        
        /* Trading Layout */
        .trading-layout {
          display: grid;
          grid-template-columns: 1fr 350px 350px;
          gap: 24px;
        }
        
        .market-panel,
        .trade-panel,
        .holdings-panel {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          overflow: hidden;
        }
        
        .panel-header {
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .panel-header h3 {
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        /* Market Panel */
        .market-tabs {
          display: flex;
          gap: 4px;
        }
        
        .market-tab {
          padding: 6px 12px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: var(--text-muted);
          font-size: 12px;
          cursor: pointer;
        }
        
        .market-tab.active {
          background: var(--bg-subtle);
          color: var(--text-primary);
        }
        
        .assets-list {
          max-height: 500px;
          overflow-y: auto;
        }
        
        .asset-item {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          background: transparent;
          border: none;
          border-bottom: 1px solid var(--border);
          cursor: pointer;
          text-align: left;
          color: var(--text-primary);
          transition: background-color 0.2s;
        }
        
        .asset-item:hover {
          background: var(--bg-subtle);
        }
        
        .asset-item.selected {
          background: rgba(59, 130, 246, 0.1);
          border-left: 3px solid var(--primary);
        }
        
        .asset-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .asset-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
        }
        
        .asset-icon-placeholder {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary);
          border-radius: 8px;
          font-weight: 700;
          font-size: 12px;
        }
        
        .asset-icon-placeholder.lg {
          width: 48px;
          height: 48px;
          font-size: 18px;
        }
        
        .asset-info .symbol {
          display: block;
          font-family: var(--font-mono);
          font-weight: 600;
          font-size: 13px;
        }
        
        .asset-info .name {
          display: block;
          font-size: 11px;
          color: var(--text-muted);
        }
        
        .asset-price {
          text-align: right;
        }
        
        .asset-price .price {
          display: block;
          font-family: var(--font-mono);
          font-size: 13px;
        }
        
        .asset-price .change {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 2px;
          font-family: var(--font-mono);
          font-size: 11px;
        }
        
        .change.positive { color: var(--success); }
        .change.negative { color: var(--error); }
        
        /* Trade Panel */
        .trade-form {
          padding: 20px;
        }
        
        .selected-asset-display {
          background: var(--bg-subtle);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
        }
        
        .asset-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        
        .asset-icon-lg {
          width: 48px;
          height: 48px;
          border-radius: 50%;
        }
        
        .asset-header h4 {
          font-size: 1.25rem;
          margin: 0;
        }
        
        .asset-header p {
          color: var(--text-muted);
          font-size: 13px;
          margin: 0;
        }
        
        .current-price {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .current-price .label {
          color: var(--text-muted);
          font-size: 13px;
        }
        
        .current-price .price {
          font-family: var(--font-mono);
          font-size: 1.5rem;
          font-weight: 600;
        }
        
        .quantity-input {
          margin-bottom: 20px;
        }
        
        .quantity-input label {
          display: block;
          font-size: 13px;
          color: var(--text-muted);
          margin-bottom: 8px;
        }
        
        .input-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .input-wrapper button {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          cursor: pointer;
        }
        
        .input-wrapper input {
          flex: 1;
          padding: 12px;
          background: var(--bg-subtle);
          border: 1px solid var(--border);
          border-radius: 8px;
          color: var(--text-primary);
          font-family: var(--font-mono);
          font-size: 18px;
          text-align: center;
        }
        
        .input-wrapper input:focus {
          outline: none;
          border-color: var(--primary);
        }
        
        .total-cost {
          text-align: center;
          margin-top: 8px;
          font-family: var(--font-mono);
          color: var(--text-secondary);
        }
        
        .trade-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        
        .trade-buttons .btn {
          padding: 16px;
          font-size: 16px;
          border-radius: 8px;
        }
        
        .no-asset-selected {
          padding: 60px 20px;
          text-align: center;
          color: var(--text-muted);
        }
        
        .no-asset-selected svg {
          margin-bottom: 16px;
          opacity: 0.3;
        }
        
        /* Holdings Panel */
        .holdings-list {
          max-height: 200px;
          overflow-y: auto;
        }
        
        .holding-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          border-bottom: 1px solid var(--border);
        }
        
        .holding-info .symbol {
          display: block;
          font-family: var(--font-mono);
          font-weight: 600;
          font-size: 14px;
        }
        
        .holding-info .quantity {
          display: block;
          font-size: 12px;
          color: var(--text-muted);
        }
        
        .holding-value {
          text-align: right;
        }
        
        .holding-value .current {
          display: block;
          font-family: var(--font-mono);
          font-size: 14px;
        }
        
        .holding-value .pnl {
          display: block;
          font-family: var(--font-mono);
          font-size: 12px;
        }
        
        .pnl.positive { color: var(--success); }
        .pnl.negative { color: var(--error); }
        
        .no-holdings {
          padding: 40px 20px;
          text-align: center;
          color: var(--text-muted);
        }
        
        /* History */
        .history-section {
          border-top: 1px solid var(--border);
          margin-top: 16px;
        }
        
        .history-list {
          max-height: 200px;
          overflow-y: auto;
        }
        
        .history-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 20px;
          border-bottom: 1px solid var(--border);
          font-size: 13px;
        }
        
        .history-item.buy .action { color: var(--success); }
        .history-item.sell .action { color: var(--error); }
        
        .trade-info .action {
          font-weight: 600;
          margin-right: 8px;
        }
        
        .trade-info .details {
          color: var(--text-muted);
        }
        
        .history-item .total {
          font-family: var(--font-mono);
        }
        
        .no-history {
          padding: 20px;
          text-align: center;
          color: var(--text-muted);
          font-size: 13px;
        }
        
        /* Loading */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          color: var(--text-muted);
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 1200px) {
          .trading-layout {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 768px) {
          .portfolio-summary {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .trading-header {
            flex-direction: column;
            gap: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default PaperTrading;